import classNames from 'classnames';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { LatLng } from 'src/types/main';
import PlacesListOption from 'src/view/components/AddressSearch/PlacesListOption';
import { ISearchInputProps, SearchInput } from '~/view/components/Controls/Inputs';
import './style.scss';

export type TGoogleAddress = {
  placeId: string;
  desc: string;
};

type TAddressSelectionCallback = (addr: string, location?: LatLng) => void;

type TSearchInputProps = Omit<ISearchInputProps, 'className' | 'contentClassName' | 'inputClassName'>;
interface IProps extends Partial<TSearchInputProps> {
  onChangeAddress: TAddressSelectionCallback;
  selectionOnEmptyValue?: boolean;
  className?: string;
  listWrapperClassName?: string;
  classNameForInput?: string;
  contentClassNameForInput?: string;
  inputClassNameForInput?: string;
}

/**
 * @prop {number} selectionOnEmptyValue - Allows to trigger callback on empty value
 * @prop {TAddressSelectionCallback} onChangeAddress - Callback on address selection
 */
const AddressSearch: FC<IProps> = ({
  value,
  defaultValue,
  onChangeAddress,
  selectionOnEmptyValue,
  placeholder = 'txt_address',
  hideSearchIcon = true,
  className,
  listWrapperClassName,
  classNameForInput,
  contentClassNameForInput,
  inputClassNameForInput,
  ...props
}: IProps): JSX.Element => {
  const [searchValue, setSearchValue] = useState<string>(String(value || defaultValue || ''));
  const [loading, setLoading] = useState<boolean>(false);
  const [autocompletedList, setAutoCompletedList] = useState<TGoogleAddress[]>([]);

  useEffect(() => {
    setSearchValue(String(value || ''));
  }, [value]);

  /** need for rendering google prediction attributes */
  const predictionsContainer = useRef<HTMLDivElement>(null);

  const onSelectAddress = useCallback((address: string) => {
    const container = predictionsContainer.current;

    if (!container) return;

    setLoading(true);

    const placesService = new google.maps.places.PlacesService(container);
    placesService.findPlaceFromQuery({ fields: ['geometry'], query: address }, (places) => {
      if (!places?.length) {
        setLoading(false);
        return;
      }

      const placeLocation = places[0].geometry?.location;

      if (placeLocation) {
        setLoading(false);
        onChangeAddress(address, { lat: placeLocation.lat(), lng: placeLocation.lng() });
      }
    });

    setSearchValue(address);
    setAutoCompletedList([]);
  }, []);

  const getAutocompletedAddresses = (text: string) => {
    // trigger callback on empty value
    if (!text && selectionOnEmptyValue) {
      onChangeAddress(text);
    }

    // clear autocompleted list if search value is empty
    if (!text) {
      setAutoCompletedList([]);
      return;
    }

    setLoading(true);

    const autocompleteService = new google.maps.places.AutocompleteService();
    autocompleteService.getPlacePredictions({ input: text }, (addresses: any) => {
      if (!addresses?.length) {
        setAutoCompletedList([]);
        setLoading(false);
        return;
      }

      setAutoCompletedList(addresses.map((place: any) => ({ placeId: place.place_id, desc: place.description })));
      setLoading(false);
    });
  };

  const onSearch = (value: string | undefined) => {
    setSearchValue(value || '');
    getAutocompletedAddresses(value || '');
  };

  return (
    <div className={classNames('address-search', className)}>
      <SearchInput
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        value={searchValue}
        onChangeValue={onSearch}
        placeholder={placeholder}
        hideSearchIcon={hideSearchIcon}
        loading={loading || props.loading}
        className={classNames('full-width', classNameForInput)}
        contentClassName={classNames('address-search__input-content', contentClassNameForInput)}
        inputClassName={inputClassNameForInput}
      />

      {autocompletedList.length ? (
        <div
          ref={predictionsContainer}
          className={classNames('address-search__list-wrapper animate__faster animate__animated animate__fadeInRight', listWrapperClassName)}
        >
          <div className="address-search__list scrollable-block__scroll_small">
            {autocompletedList.map((addr, index) => (
              <PlacesListOption key={index} address={addr} onSelect={onSelectAddress} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default AddressSearch;
